import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"

export type CustomerPickerProps = Omit<SelectorTriggerProps, "onSelect"> & {
  onSelect(customer: CustomerFragment): void
  /** Pre-seeds the search — e.g. the team name when picking the customer
   *  of a team being created, so a same-named customer is one click. */
  defaultSearch?: string
}

function CustomerPicker(props: CustomerPickerProps) {
  const { onSelect, defaultSearch, ...triggerProps } = props

  const [search, setSearch] = useState(defaultSearch ?? "")

  const { data, error, loading } = useCustomerQuery({
    skip: search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for customers",
  })

  const customers = data?.getCustomers.items ?? []

  return (
    <Selector>
      <Selector.Trigger iconSrc="search" {...triggerProps}>
        {triggerProps.children ?? "Pick a customer..."}
      </Selector.Trigger>

      <Selector.Content>
        <Selector.Search
          placeholder="Search by customer name..."
          onSearch={setSearch}
          defaultValue={defaultSearch}
        />

        <Selector.List
          emptyLabel="No customers found"
          isLoading={loading}
          error={error}
          noResults={customers.length === 0}
        >
          {customers.map((customer) => (
            <Selector.Item
              key={customer.Id}
              value={String(customer.Id)}
              onSelect={() => {
                onSelect(customer)
              }}
            >
              <Selector.ItemText
                primary={customer.Name ?? "Unnamed"}
                secondary={customer.ExternalCustomerId}
              />
            </Selector.Item>
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default CustomerPicker
